// A dashboard: filters, then the widgets on a 12-column grid. In edit mode
// widgets are dragged and resized, and edited in the side panel; the draft
// is drawn by the same renderer as saved versions, exports and reports.
import 'react-grid-layout/css/styles.css'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import ReactGridLayout, { verticalCompactor, type Layout } from 'react-grid-layout'
import { Link, useParams, useSearchParams } from 'react-router'

import { api, can } from '../api'
import { CelEditor } from '../cel/CelEditor'
import { ErrorBox } from '../components'
import {
  dash,
  datePresets,
  download,
  palettes,
  widgetKinds,
  type ChartableBucket,
  type DashCheck,
  type DashboardFilter,
  type DashboardSpec,
  type Measure,
  type NamedExpr,
  type Source,
  type Values,
  type Widget,
} from '../dashApi'
import { safeSvg } from '../safeSvg'
import { HistoryLink } from './history'

const ROW = 30
const MARGIN = 10

function subscribeTheme(cb: () => void) {
  const mo = new MutationObserver(cb)
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
  return () => mo.disconnect()
}
const currentTheme = () => (document.documentElement.dataset.theme === 'light' ? 'light' : 'dark')

const identOf = (s: string, fallback: string) => {
  const id = s
    .toLowerCase()
    .replace(/[åä]/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^[^a-z]+/, '')
    .replace(/_+$/, '')
    .slice(0, 40)
  return id || fallback
}

function defaultValues(spec: DashboardSpec | undefined): Values {
  const v: Values = {}
  for (const f of spec?.filters ?? []) v[f.id] = f.default ?? (f.kind === 'date_range' ? 'all' : '')
  return v
}

export function DashboardPage() {
  const { slug = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const editor = can(me.data, 'editor')
  const q = useQuery({ queryKey: ['dashboard', slug], queryFn: () => dash.get('dashboard', slug) })
  const saved = q.data?.versions[0]?.body as DashboardSpec | undefined
  const [draft, setDraft] = useState<DashboardSpec | null>(null)
  const spec = draft ?? saved
  const editing = params.get('edit') === '1' && editor
  const [selected, setSelected] = useState<string | null>(() => params.get('widget'))
  const [tab, setTab] = useState<'widget' | 'dashboard' | 'json'>('widget')
  const [values, setValues] = useState<Values | null>(null)
  const vals = values ?? defaultValues(spec)
  const theme = useSyncExternalStore(subscribeTheme, currentTheme)
  const [comment, setComment] = useState('')

  // Saved versions are drawn by slug; an unsaved draft is sent along.
  const src: Source = draft ? { spec: draft } : { slug }
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(saved)

  // Rebuilt buckets bump the widgets.
  const fresh = useQuery({
    queryKey: ['dash-fresh', slug],
    queryFn: () => dash.freshness(slug),
    refetchInterval: Math.max(spec?.refresh_seconds || 30, 30) * 1000,
    enabled: !!saved,
  })
  const freshKey = JSON.stringify(fresh.data ?? {})

  const check = useQuery({
    queryKey: ['dash-check', JSON.stringify(draft)],
    queryFn: () => dash.check('dashboard', draft),
    enabled: dirty,
  })
  const report: DashCheck | null | undefined = dirty ? check.data : q.data?.check

  const save = useMutation({
    mutationFn: () => dash.save('dashboard', slug, draft, comment),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['dashboard', slug] })
      setDraft(null)
      setComment('')
    },
  })
  const pdf = useMutation({
    mutationFn: async () => download(await dash.pdf(src, vals, 'sv'), `${slug}.pdf`),
  })

  const { width, containerRef } = useWidth()
  const mounted = width > 0
  if (q.error) return <ErrorBox error={q.error} />
  if (!spec) return <p className="muted">Loading…</p>

  const update = (fn: (s: DashboardSpec) => DashboardSpec) => setDraft(fn(structuredClone(spec)))
  const setWidget = (id: string, fn: (w: Widget) => Widget) =>
    update((s) => ({ ...s, widgets: s.widgets.map((w) => (w.id === id ? fn(w) : w)) }))
  const addWidget = () => {
    const bottom = Math.max(0, ...spec.widgets.map((w) => w.layout.y + w.layout.h))
    let n = spec.widgets.length + 1
    while (spec.widgets.some((w) => w.id === `widget_${n}`)) n++
    const w: Widget = { id: `widget_${n}`, title: 'New widget', kind: 'bar', bucket: '', dimensions: [], measures: [], layout: { x: 0, y: bottom, w: 6, h: 7 } }
    update((s) => ({ ...s, widgets: [...s.widgets, w] }))
    setSelected(w.id)
    setTab('widget')
  }
  const layout: Layout = spec.widgets.map((w) => ({ i: w.id, x: w.layout.x, y: w.layout.y, w: w.layout.w, h: w.layout.h, minW: 2, minH: 2 }))
  const onLayout = (l: Layout) => {
    const moved = spec.widgets.some((w) => {
      const p = l.find((x) => x.i === w.id)
      return p && (p.x !== w.layout.x || p.y !== w.layout.y || p.w !== w.layout.w || p.h !== w.layout.h)
    })
    if (!moved) return
    update((s) => ({
      ...s,
      widgets: s.widgets.map((w) => {
        const p = l.find((x) => x.i === w.id)
        return p ? { ...w, layout: { x: p.x, y: p.y, w: p.w, h: Math.min(p.h, 30) } } : w
      }),
    }))
  }
  const problems = (id: string) => (report?.problems ?? []).filter((p) => p.widget === id)
  const general = (report?.problems ?? []).filter((p) => !p.widget)

  return (
    <section className={`page dash ${editing ? 'dash-editing' : ''}`}>
      <header className="page-h">
        <div>
          <div className="crumbs">
            <Link to="/dashboards">Dashboards</Link> / <span className="mono">{slug}</span>
          </div>
          <h1>{spec.title}</h1>
          {spec.description && <p className="sub">{spec.description}</p>}
        </div>
        <div className="row">
          {editing && <button className="btn" onClick={addWidget}>Add widget</button>}
          {editing && dirty && (
            <>
              <input className="grow" placeholder="What changed? (optional)" value={comment} onChange={(e) => setComment(e.target.value)} />
              <button className="btn btn-primary" disabled={save.isPending || report?.ok === false} onClick={() => save.mutate()}
                title={report?.ok === false ? 'Fix the problems first' : ''}>
                Save v{(q.data?.summary.current_version ?? 0) + 1}
              </button>
              <button className="btn" onClick={() => setDraft(null)}>Discard</button>
            </>
          )}
          <HistoryLink kind="dashboard" slug={slug} />
          <button className="btn" disabled={pdf.isPending} onClick={() => pdf.mutate()}>
            {pdf.isPending ? 'Exporting…' : 'Export PDF'}
          </button>
          {editor && (
            <button className="btn" onClick={() => setParams(editing ? {} : { edit: '1' })}>
              {editing ? 'Done' : 'Edit'}
            </button>
          )}
        </div>
      </header>
      {save.error && <ErrorBox error={save.error} />}
      {pdf.error && <ErrorBox error={pdf.error} />}
      {general.length > 0 && (
        <ul className="callout err">
          {general.map((p, i) => (
            <li key={i}>
              {p.path}: {p.message}
            </li>
          ))}
        </ul>
      )}
      <FilterBar src={src} spec={spec} values={vals} onChange={setValues} />
      <div className="dash-body">
        <div ref={containerRef} className="dash-grid">
          {spec.widgets.length === 0 && (
            <p className="muted">{editing ? 'No widgets yet: Add widget.' : 'No widgets yet. Edit the dashboard to add some.'}</p>
          )}
          {mounted && (
            <ReactGridLayout
              width={width}
              layout={layout}
              gridConfig={{ cols: 12, rowHeight: ROW, margin: [MARGIN, MARGIN] }}
              dragConfig={{ enabled: editing, handle: '.wg-h' }}
              resizeConfig={{ enabled: editing }}
              compactor={verticalCompactor}
              onLayoutChange={onLayout}
            >
              {spec.widgets.map((w) => (
                <div key={w.id} className={`wg ${selected === w.id && editing ? 'wg-on' : ''}`}>
                  <div className="wg-h">
                    <span className="wg-t">{w.title}</span>
                    <span className="row">
                      {problems(w.id).length > 0 && <span className="pill pill-err">{problems(w.id).length} problem(s)</span>}
                      {editing && (
                        <button className="btn btn-sm" onMouseDown={(e) => e.stopPropagation()} onClick={() => { setSelected(w.id); setTab('widget') }}>
                          Edit
                        </button>
                      )}
                      <button className="btn btn-sm" title="Download PNG" onMouseDown={(e) => e.stopPropagation()}
                        onClick={async () => download(await dash.png(src, w.id, w.layout.w * 90, w.layout.h * 40, vals), `${w.id}.png`)}>
                        PNG
                      </button>
                    </span>
                  </div>
                  <WidgetView src={src} widget={w} values={vals} theme={theme} freshKey={freshKey} broken={problems(w.id).length > 0} />
                </div>
              ))}
            </ReactGridLayout>
          )}
        </div>
        {editing && (
          <aside className="dash-side card">
            <div className="tabs">
              {(['widget', 'dashboard', 'json'] as const).map((t) => (
                <button key={t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
                  {t === 'widget' ? 'Widget' : t === 'dashboard' ? 'Dashboard' : 'JSON'}
                </button>
              ))}
            </div>
            {tab === 'widget' &&
              (selected && spec.widgets.find((w) => w.id === selected) ? (
                <WidgetEditor
                  widget={spec.widgets.find((w) => w.id === selected)!}
                  spec={spec}
                  problems={problems(selected)}
                  onChange={(fn) => setWidget(selected, fn)}
                  onDelete={() => {
                    update((s) => ({ ...s, widgets: s.widgets.filter((w) => w.id !== selected) }))
                    setSelected(null)
                  }}
                />
              ) : (
                <p className="muted">Pick a widget (Edit on its header) or add one.</p>
              ))}
            {tab === 'dashboard' && <SettingsEditor spec={spec} onChange={(s) => setDraft(s)} />}
            {tab === 'json' && <JsonEditor spec={spec} onChange={(s) => setDraft(s)} />}
          </aside>
        )}
      </div>
    </section>
  )
}

/** The width of an element that may mount later (a callback ref). */
function useWidth() {
  const [width, setWidth] = useState(0)
  const ro = useRef<ResizeObserver | null>(null)
  const containerRef = useCallback((el: HTMLDivElement | null) => {
    ro.current?.disconnect()
    if (!el) return
    ro.current = new ResizeObserver(([e]) => setWidth(Math.floor(e.contentRect.width)))
    ro.current.observe(el)
    setWidth(Math.floor(el.getBoundingClientRect().width))
  }, [])
  return { width, containerRef }
}

function FilterBar({ src, spec, values, onChange }: { src: Source; spec: DashboardSpec; values: Values; onChange: (v: Values) => void }) {
  if (!spec.filters?.length) return null
  return (
    <div className="dash-filters">
      {spec.filters.map((f) =>
        f.kind === 'date_range' ? (
          <DateFilter key={f.id} f={f} value={values[f.id] ?? 'all'} onChange={(v) => onChange({ ...values, [f.id]: v })} />
        ) : (
          <SelectFilter key={f.id} src={src} f={f} value={values[f.id] ?? ''} onChange={(v) => onChange({ ...values, [f.id]: v })} />
        ),
      )}
    </div>
  )
}

const addDay = (d: string, n: number) => {
  const t = new Date(d + 'T00:00:00Z')
  t.setUTCDate(t.getUTCDate() + n)
  return t.toISOString().slice(0, 10)
}

function DateFilter({ f, value, onChange }: { f: DashboardFilter; value: string; onChange: (v: string) => void }) {
  const custom = value.includes('..')
  const [from, toEx] = custom ? value.split('..') : ['', '']
  return (
    <label className="dash-filter">
      <span className="muted">{f.label || f.id}</span>
      <select value={custom ? 'custom' : value} onChange={(e) => {
        if (e.target.value !== 'custom') return onChange(e.target.value)
        const today = new Date().toISOString().slice(0, 10)
        onChange(`${addDay(today, -30)}..${addDay(today, 1)}`)
      }}>
        {datePresets.map((p) => (
          <option key={p.value} value={p.value}>
            {p.label}
          </option>
        ))}
        <option value="custom">Custom…</option>
      </select>
      {custom && (
        <>
          <input type="date" value={from} onChange={(e) => e.target.value && onChange(`${e.target.value}..${toEx}`)} />
          <span className="muted">to</span>
          <input type="date" value={addDay(toEx, -1)} onChange={(e) => e.target.value && onChange(`${from}..${addDay(e.target.value, 1)}`)} />
        </>
      )}
    </label>
  )
}

function SelectFilter({ src, f, value, onChange }: { src: Source; f: DashboardFilter; value: string; onChange: (v: string) => void }) {
  const opts = useQuery({ queryKey: ['dash-opts', JSON.stringify(src), f.id], queryFn: () => dash.options(src, f.id), staleTime: 60_000 })
  return (
    <label className="dash-filter">
      <span className="muted">{f.label || f.id}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">All</option>
        {(opts.data ?? []).map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  )
}

function WidgetView({ src, widget, values, theme, freshKey, broken }: {
  src: Source
  widget: Widget
  values: Values
  theme: 'light' | 'dark'
  freshKey: string
  broken: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ w: 0, h: 0 })
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(([e]) => {
      const w = Math.round(e.contentRect.width / 20) * 20
      const h = Math.round(e.contentRect.height / 20) * 20
      setSize((s) => (s.w === w && s.h === h ? s : { w, h }))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const key = 'spec' in src ? JSON.stringify(widget) + JSON.stringify(src.spec.filters ?? []) + src.spec.palette : `${src.slug}:${src.version ?? 0}`
  const q = useQuery({
    queryKey: ['widget-svg', key, widget.id, size.w, size.h, theme, JSON.stringify(values), freshKey],
    queryFn: () => dash.svg(src, widget.id, size.w, size.h, theme, values),
    enabled: size.w >= 120 && size.h >= 60 && !broken && !!widget.bucket,
    placeholderData: (prev) => prev,
    staleTime: 5 * 60_000,
  })
  return (
    <div className="wg-b" ref={ref}>
      {!widget.bucket ? (
        <p className="muted">Pick a bucket in the side panel.</p>
      ) : broken ? (
        <p className="muted">Fix the problems in the side panel.</p>
      ) : q.error ? (
        <p className="callout err">{q.error.message}</p>
      ) : q.data ? (
        <SvgBox svg={q.data.svg} />
      ) : (
        <p className="muted">Loading…</p>
      )}
    </div>
  )
}

/** Shows a renderer SVG rebuilt from the allow-list (safeSvg). */
function SvgBox({ svg }: { svg: string }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const node = safeSvg(svg)
    el.replaceChildren(...(node ? [node] : []))
  }, [svg])
  return <div className="wg-svg" ref={ref} />
}

function useBuckets() {
  return useQuery({ queryKey: ['dash-buckets'], queryFn: dash.buckets, staleTime: 5 * 60_000 })
}

function WidgetEditor({ widget, spec, problems, onChange, onDelete }: {
  widget: Widget
  spec: DashboardSpec
  problems: { path: string; message: string }[]
  onChange: (fn: (w: Widget) => Widget) => void
  onDelete: () => void
}) {
  const buckets = useBuckets()
  const fns = useQuery({ queryKey: ['cel-functions'], queryFn: api.celFunctions, staleTime: Infinity })
  const bucket: ChartableBucket | undefined = buckets.data?.find((b) => b.slug === widget.bucket)
  const set = <K extends keyof Widget>(k: K, v: Widget[K]) => onChange((w) => ({ ...w, [k]: v }))
  const style = widget.style ?? {}
  const setStyle = (k: keyof NonNullable<Widget['style']>, v: unknown) => onChange((w) => ({ ...w, style: { ...(w.style ?? {}), [k]: v } }))
  const checkExpr = (src: string) => dash.expr(widget.bucket, src)
  const exprList = (key: 'dimensions' | 'measures', label: string, hint: string) => {
    const list = (widget[key] ?? []) as (NamedExpr | Measure)[]
    return (
      <div className="dash-exprs">
        <div className="row-between">
          <strong>{label}</strong>
          <button className="btn btn-sm" disabled={!bucket} onClick={() => {
            const first = bucket?.columns[0]?.name ?? 'x'
            const next = key === 'measures' ? { name: `m${list.length + 1}`, expr: 'count()' } : { name: identOf(first, `d${list.length + 1}`), expr: first }
            set(key, [...list, next] as Widget[typeof key])
          }}>
            Add
          </button>
        </div>
        <p className="note">{hint}</p>
        {list.map((e, i) => (
          <div key={i} className="dash-expr">
            <div className="row">
              <input className="mono" value={e.name} aria-label={`${label} ${i + 1} name`}
                onChange={(ev) => set(key, list.map((x, j) => (j === i ? { ...x, name: identOf(ev.target.value, x.name) } : x)) as Widget[typeof key])} />
              {key === 'measures' && (
                <select value={(e as Measure).format ?? ''} aria-label="Number format"
                  onChange={(ev) => set(key, list.map((x, j) => (j === i ? { ...x, format: (ev.target.value || undefined) as Measure['format'] } : x)) as Widget[typeof key])}>
                  <option value="">auto</option>
                  <option value="integer">integer</option>
                  <option value="number">number</option>
                  <option value="percent">percent</option>
                  <option value="sek">kr</option>
                  <option value="compact">compact</option>
                </select>
              )}
              <button className="btn btn-sm" aria-label="Remove" onClick={() => set(key, list.filter((_, j) => j !== i) as Widget[typeof key])}>×</button>
            </div>
            {bucket && (
              <CelEditor value={e.expr} check={checkExpr} functions={fns.data?.functions ?? []} ariaLabel={`${label} ${i + 1} expression`}
                onChange={(v) => set(key, list.map((x, j) => (j === i ? { ...x, expr: v } : x)) as Widget[typeof key])} />
            )}
          </div>
        ))}
      </div>
    )
  }
  return (
    <div className="form dash-form">
      {problems.length > 0 && (
        <ul className="callout err">
          {problems.map((p, i) => (
            <li key={i}>
              {p.path}: {p.message}
            </li>
          ))}
        </ul>
      )}
      <label>
        Title
        <input value={widget.title} onChange={(e) => set('title', e.target.value)} />
      </label>
      <div className="form-grid">
        <label>
          Kind
          <select value={widget.kind} onChange={(e) => onChange((w) => {
            const kind = e.target.value as Widget['kind']
            return { ...w, kind, map: kind === 'map' ? (w.map ?? { level: 'lan', key: 'code' }) : undefined, style: kind === 'stat' ? w.style : { ...(w.style ?? {}), trend: undefined } }
          })}>
            {widgetKinds.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Bucket
          <select value={widget.bucket} onChange={(e) => set('bucket', e.target.value)}>
            <option value="">Choose…</option>
            {(buckets.data ?? []).map((b) => (
              <option key={b.slug} value={b.slug}>
                {b.title || b.slug} {b.org_id === 'aleva' ? '' : `(${b.org_id})`}
              </option>
            ))}
          </select>
        </label>
      </div>
      {bucket && (
        <p className="note">
          Fields: {bucket.columns.map((c) => <code key={c.name} title={c.type}>{c.name}</code>)}
        </p>
      )}
      {exprList('dimensions', 'Dimensions', widget.kind === 'map' ? 'The region: a län/kommun code or name.' : widget.kind === 'stat' ? 'Optional: a date for the sparkline.' : 'The x axis first, then (optional) one series per value. E.g. date_trunc(\'month\', day).')}
      {exprList('measures', 'Measures', 'Aggregates: count(), sum(x), avg(x), count_distinct(x) …')}
      <label>
        Only rows where (optional)
        {bucket && <CelEditor value={widget.where ?? ''} check={checkExpr} functions={fns.data?.functions ?? []} placeholder="e.g. region != 'Gotland'"
          onChange={(v) => set('where', v || undefined)} ariaLabel="Row condition" />}
      </label>
      <div className="form-grid">
        <label>
          Sort by
          <select value={widget.sort?.[0]?.name ?? ''} onChange={(e) => set('sort', e.target.value ? [{ name: e.target.value, desc: widget.sort?.[0]?.desc }] : undefined)}>
            <option value="">default</option>
            {[...(widget.dimensions ?? []), ...(widget.measures ?? [])].map((x) => (
              <option key={x.name} value={x.name}>
                {x.name}
              </option>
            ))}
          </select>
        </label>
        <label className="check">
          <input type="checkbox" checked={!!widget.sort?.[0]?.desc} disabled={!widget.sort?.length}
            onChange={(e) => set('sort', widget.sort?.length ? [{ ...widget.sort[0], desc: e.target.checked }] : undefined)} />
          Descending
        </label>
        <label>
          Row limit
          <input type="number" min={0} max={5000} value={widget.limit ?? 0} onChange={(e) => set('limit', Number(e.target.value) || undefined)} />
        </label>
      </div>
      {widget.kind === 'map' && (
        <div className="form-grid">
          <label>
            Regions
            <select value={widget.map?.level ?? 'lan'} onChange={(e) => set('map', { level: e.target.value as 'lan' | 'kommun', key: widget.map?.key ?? 'code' })}>
              <option value="lan">Län (21)</option>
              <option value="kommun">Kommuner (290)</option>
            </select>
          </label>
          <label>
            Matched by
            <select value={widget.map?.key ?? 'code'} onChange={(e) => set('map', { level: widget.map?.level ?? 'lan', key: e.target.value as 'code' | 'name' })}>
              <option value="code">SCB code (01, 0180)</option>
              <option value="name">Name</option>
            </select>
          </label>
        </div>
      )}
      <details>
        <summary>Style</summary>
        <div className="form-grid">
          <label>
            X axis label
            <input value={style.x_label ?? ''} onChange={(e) => setStyle('x_label', e.target.value || undefined)} />
          </label>
          <label>
            Y axis label
            <input value={style.y_label ?? ''} onChange={(e) => setStyle('y_label', e.target.value || undefined)} />
          </label>
          <label className="wide">
            Colours (comma-separated hex, in series order; empty uses the palette)
            <input className="mono" value={(style.colors ?? []).join(', ')}
              onChange={(e) => setStyle('colors', e.target.value.split(',').map((x) => x.trim()).filter(Boolean).length ? e.target.value.split(',').map((x) => x.trim()).filter(Boolean) : undefined)} />
          </label>
          {(['hide_legend', 'show_values', 'smooth', 'horizontal', 'trend'] as const)
            .filter((k) => (k === 'trend' ? widget.kind === 'stat' : k === 'horizontal' ? widget.kind === 'bar' || widget.kind === 'stacked' : k === 'smooth' ? widget.kind === 'line' || widget.kind === 'area' : widget.kind !== 'stat' && widget.kind !== 'table'))
            .map((k) => (
              <label key={k} className="check">
                <input type="checkbox" checked={!!style[k]} onChange={(e) => setStyle(k, e.target.checked || undefined)} />
                {{ hide_legend: 'Hide legend', show_values: 'Show values', smooth: 'Smooth lines', horizontal: 'Horizontal bars', trend: 'Trend vs previous period' }[k]}
              </label>
            ))}
        </div>
      </details>
      {(spec.filters ?? []).length > 0 && (
        <details>
          <summary>Dashboard filters</summary>
          {(spec.filters ?? []).map((f) => (
            <label key={f.id} className="check">
              <input type="checkbox" checked={!(widget.ignore_filters ?? []).includes(f.id)}
                onChange={(e) => set('ignore_filters', e.target.checked ? (widget.ignore_filters ?? []).filter((x) => x !== f.id) : [...(widget.ignore_filters ?? []), f.id])} />
              {f.label || f.id} <span className="muted mono">({f.field})</span>
            </label>
          ))}
          <p className="note">A filter applies when the widget's bucket has its field.</p>
        </details>
      )}
      <div className="row-between">
        <span className="muted mono">id {widget.id}</span>
        <button className="btn btn-sm" onClick={onDelete}>
          Remove widget
        </button>
      </div>
    </div>
  )
}

function SettingsEditor({ spec, onChange }: { spec: DashboardSpec; onChange: (s: DashboardSpec) => void }) {
  const buckets = useBuckets()
  const fields = useMemo(() => {
    const used = new Set(spec.widgets.map((w) => w.bucket))
    const out = new Map<string, string>()
    for (const b of buckets.data ?? []) if (used.has(b.slug)) for (const c of b.columns) out.set(c.name, c.type)
    return [...out.entries()]
  }, [buckets.data, spec.widgets])
  const filters = spec.filters ?? []
  const setFilter = (i: number, f: Partial<DashboardFilter>) => onChange({ ...spec, filters: filters.map((x, j) => (j === i ? { ...x, ...f } : x)) })
  return (
    <div className="form dash-form">
      <label>
        Title
        <input value={spec.title} onChange={(e) => onChange({ ...spec, title: e.target.value })} />
      </label>
      <label>
        Description
        <textarea rows={2} value={spec.description ?? ''} onChange={(e) => onChange({ ...spec, description: e.target.value || undefined })} />
      </label>
      <div className="form-grid">
        <label>
          Palette
          <select value={spec.palette ?? 'aleva'} onChange={(e) => onChange({ ...spec, palette: e.target.value })}>
            {palettes.map((p) => (
              <option key={p}>{p}</option>
            ))}
          </select>
        </label>
        <label>
          Live refresh (seconds, 0 = on rebuild)
          <input type="number" min={0} value={spec.refresh_seconds ?? 0} onChange={(e) => onChange({ ...spec, refresh_seconds: Number(e.target.value) || undefined })} />
        </label>
      </div>
      <div className="row-between">
        <strong>Filters</strong>
        <button className="btn btn-sm" onClick={() => {
          const date = fields.find(([, t]) => t.startsWith('DATE') || t.startsWith('TIMESTAMP'))
          onChange({ ...spec, filters: [...filters, date
            ? { id: `f${filters.length + 1}`, label: 'Period', kind: 'date_range', field: date[0], default: 'last_12_months' }
            : { id: `f${filters.length + 1}`, label: 'Filter', kind: 'select', field: fields[0]?.[0] ?? '' }] })
        }}>
          Add filter
        </button>
      </div>
      {filters.map((f, i) => (
        <div key={i} className="dash-expr">
          <div className="form-grid">
            <label>
              Label
              <input value={f.label} onChange={(e) => setFilter(i, { label: e.target.value })} />
            </label>
            <label>
              Kind
              <select value={f.kind} onChange={(e) => setFilter(i, { kind: e.target.value as DashboardFilter['kind'], default: undefined })}>
                <option value="date_range">Date range</option>
                <option value="select">Value</option>
              </select>
            </label>
            <label>
              Field
              <select value={f.field} onChange={(e) => setFilter(i, { field: e.target.value })}>
                {!fields.some(([n]) => n === f.field) && <option value={f.field}>{f.field}</option>}
                {fields
                  .filter(([, t]) => f.kind === 'select' || t.startsWith('DATE') || t.startsWith('TIMESTAMP'))
                  .map(([n, t]) => (
                    <option key={n} value={n}>
                      {n} ({t})
                    </option>
                  ))}
              </select>
            </label>
            {f.kind === 'date_range' && (
              <label>
                Default
                <select value={f.default ?? 'all'} onChange={(e) => setFilter(i, { default: e.target.value })}>
                  {datePresets.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <button className="btn btn-sm" onClick={() => onChange({ ...spec, filters: filters.filter((_, j) => j !== i) })}>
            Remove filter
          </button>
        </div>
      ))}
    </div>
  )
}

function JsonEditor({ spec, onChange }: { spec: DashboardSpec; onChange: (s: DashboardSpec) => void }) {
  const [text, setText] = useState(() => JSON.stringify(spec, null, 2))
  const [err, setErr] = useState('')
  return (
    <div className="form">
      <textarea className="mono" rows={28} value={text} onChange={(e) => setText(e.target.value)} spellCheck={false} />
      {err && <p className="callout err">{err}</p>}
      <button className="btn" onClick={() => {
        try {
          onChange(JSON.parse(text) as DashboardSpec)
          setErr('')
        } catch (e) {
          setErr(String(e))
        }
      }}>
        Apply JSON
      </button>
    </div>
  )
}
